/**
 * Shared severity rubric for distributor-vs-Part diffing.
 *
 * Used by both:
 * - `ValidateAgainstDistributorModal` (per-part interactive review)
 * - the bulk-refresh flow (auto-apply non-structural, flag structural)
 *
 * Mirror this list in `backend/app/services/sourcing_diff.py` —
 * the bulk endpoint needs the same classification server-side so a
 * field's severity decision is consistent regardless of where the
 * apply happens. Keep the two in sync when adding new spec columns.
 *
 * Severity meanings:
 * - **structural**: identity columns where vendor disagreement
 *   usually means "wrong supplier linked to this part" (mfg_pn,
 *   manufacturer, package). Pre-checked OFF in the validate modal;
 *   triggers needs_review status in bulk-refresh (no silent apply).
 * - **commercial**: numeric/categorical specs designers actively
 *   filter on (voltages, currents, tolerance, color, …). Pre-checked
 *   ON in validate when a real difference is found. In bulk-refresh,
 *   filled into blank Part fields only — never overwrites an existing
 *   value the user typed.
 * - **informational**: lower-stakes fields the vendor might tweak
 *   (description wording, image/datasheet URLs). Pre-checked OFF in
 *   validate. Same fill-blanks-only behavior as commercial in bulk.
 *
 * Fields not in this list aren't compared. KiCad refs, internal IDs,
 * inventory, supplier-only data (price, MOQ, stock) are out of scope —
 * supplier data flows through `PartSupplier.update_supplier`, not
 * through this rubric.
 */

import type { LookupResult } from './distributors';
import { isPartFieldHidden } from './field-relevance';
import { formatMounting, type PartCategory, type PartDetail } from './parts';

export type Severity = 'structural' | 'commercial' | 'informational';

export interface FieldDef {
  partField: keyof PartDetail;
  dtoField: keyof LookupResult;
  label: string;
  severity: Severity;
  /** Optional formatter for display. Numbers default to `.toString()`. */
  format?: (v: unknown) => string;
  /** Append this unit suffix when rendering a numeric value. */
  unit?: string;
}

export interface Diff {
  def: FieldDef;
  /** Display value for the "Current" column. May differ from the raw
   * Part attribute for fields like category where we want to render
   * a name rather than an integer id. */
  current: unknown;
  /** Display value for the vendor column. Same display-vs-stored
   * distinction as `current`. */
  vendor: unknown;
  /** Value to send on apply. Defaults to `vendor` when omitted —
   * category overrides this with the resolved id since the display
   * shows the name but the server expects the integer FK. */
  applyValue?: unknown;
  preChecked: boolean;
}

export const FIELDS: FieldDef[] = [
  // Identity — structural. Disagreement usually means the wrong
  // supplier is linked, not that the user's data is stale.
  { partField: 'mfg_pn', dtoField: 'mpn', label: 'Mfg PN', severity: 'structural' },
  { partField: 'manufacturer', dtoField: 'manufacturer', label: 'Manufacturer', severity: 'structural' },
  { partField: 'package', dtoField: 'package', label: 'Package', severity: 'structural' },
  // Mounting: the LCSC/DigiKey/Mouser adapters now infer SMD/THT from
  // the package string when the upstream's structured params don't
  // carry it explicitly. Structural severity so a disagreement (user
  // typed THT but the 0603 package implies SMD) trips review.
  // `format` upper-cases the acronyms so the diff cell reads "SMD"
  // not "smd" — same treatment as the parts list and drawer.
  {
    partField: 'mounting',
    dtoField: 'mounting',
    label: 'Mounting',
    severity: 'structural',
    format: (v) => formatMounting(typeof v === 'string' ? v : null),
  },

  // Identity-bearing specs (passives + LDO + LED). Commercial — the
  // typical "what does this part DO" columns.
  { partField: 'value', dtoField: 'value', label: 'Value', severity: 'commercial' },
  { partField: 'tolerance', dtoField: 'tolerance', label: 'Tolerance', severity: 'commercial' },
  { partField: 'dielectric', dtoField: 'dielectric', label: 'Dielectric / Tempco', severity: 'commercial' },
  { partField: 'polarity', dtoField: 'polarity', label: 'Polarity / Type', severity: 'commercial' },
  { partField: 'color', dtoField: 'color', label: 'Color', severity: 'commercial' },

  // Numeric electrical specs.
  { partField: 'voltage_v', dtoField: 'voltage_v', label: 'Voltage', severity: 'commercial', unit: 'V' },
  { partField: 'voltage_v_max', dtoField: 'voltage_v_max', label: 'V max', severity: 'commercial', unit: 'V' },
  { partField: 'current_a', dtoField: 'current_a', label: 'Current', severity: 'commercial', unit: 'A' },
  { partField: 'power_w', dtoField: 'power_w', label: 'Power', severity: 'commercial', unit: 'W' },
  { partField: 'vf', dtoField: 'vf', label: 'Vf', severity: 'commercial', unit: 'V' },
  { partField: 'vr', dtoField: 'vr', label: 'Vr', severity: 'commercial', unit: 'V' },
  { partField: 'voltage_clamp_v', dtoField: 'voltage_clamp_v', label: 'Vc clamp', severity: 'commercial', unit: 'V' },
  { partField: 'frequency_hz', dtoField: 'frequency_hz', label: 'Frequency', severity: 'commercial', unit: 'Hz' },
  { partField: 'wavelength_nm', dtoField: 'wavelength_nm', label: 'Wavelength', severity: 'commercial', unit: 'nm' },
  { partField: 'pin_count', dtoField: 'pin_count', label: 'Pin count', severity: 'commercial' },
  { partField: 'pitch_mm', dtoField: 'pitch_mm', label: 'Pitch', severity: 'commercial', unit: 'mm' },
  { partField: 'temp_min_c', dtoField: 'temp_min_c', label: 'Temp min', severity: 'commercial', unit: '°C' },
  { partField: 'temp_max_c', dtoField: 'temp_max_c', label: 'Temp max', severity: 'commercial', unit: '°C' },

  // Type-specific (regulator / fuse).
  { partField: 'output_voltage_v', dtoField: 'output_voltage_v', label: 'Output voltage', severity: 'commercial', unit: 'V' },
  { partField: 'output_voltage_v_max', dtoField: 'output_voltage_v_max', label: 'Output voltage max', severity: 'commercial', unit: 'V' },
  { partField: 'output_current_a', dtoField: 'output_current_a', label: 'Output current', severity: 'commercial', unit: 'A' },
  { partField: 'regulator_output_type', dtoField: 'regulator_output_type', label: 'Regulator type', severity: 'commercial' },
  { partField: 'hold_current_a', dtoField: 'hold_current_a', label: 'Hold current', severity: 'commercial', unit: 'A' },
  { partField: 'trip_current_a', dtoField: 'trip_current_a', label: 'Trip current', severity: 'commercial', unit: 'A' },

  // 0014 spec columns.
  { partField: 'luminous_intensity_mcd', dtoField: 'luminous_intensity_mcd', label: 'Luminous intensity', severity: 'commercial', unit: 'mcd' },
  { partField: 'load_capacitance_pf', dtoField: 'load_capacitance_pf', label: 'Load capacitance', severity: 'commercial', unit: 'pF' },
  { partField: 'rds_on_ohms', dtoField: 'rds_on_ohms', label: 'RDS(on)', severity: 'commercial', unit: 'Ω' },
  { partField: 'hfe', dtoField: 'hfe', label: 'hFE', severity: 'commercial' },

  // Informational — lower-stakes "the vendor might have polished
  // this" fields. Pre-checked OFF in validate.
  { partField: 'description', dtoField: 'description', label: 'Description', severity: 'informational' },
  { partField: 'datasheet_url', dtoField: 'datasheet_url', label: 'Datasheet URL', severity: 'informational' },
  { partField: 'image_url', dtoField: 'image_url', label: 'Image URL', severity: 'informational' },
];

export function isEmpty(v: unknown): boolean {
  if (v === null || v === undefined) return true;
  if (typeof v === 'string' && v.trim() === '') return true;
  return false;
}

export function valuesEqual(a: unknown, b: unknown): boolean {
  if (isEmpty(a) && isEmpty(b)) return true;
  if (isEmpty(a) || isEmpty(b)) return false;
  if (typeof a === 'number' && typeof b === 'number') {
    // Tolerance for floating-point — vendor data + our parser go
    // through float math, so 0.35 vs 0.35000000000000003 shouldn't
    // count as a diff.
    return Math.abs(a - b) < 1e-9 * Math.max(Math.abs(a), Math.abs(b), 1);
  }
  return String(a).trim() === String(b).trim();
}

export function buildDiffs(
  part: PartDetail,
  dto: LookupResult,
  /** Optional categories cache. When provided, the diff includes a
   * Category row whenever the part's category_id differs from the
   * vendor's resolved id, with display values rendered from the
   * categories list (so the table shows "Resistor › Thick Film"
   * rather than the raw integer ids). It also drives the category-
   * aware filter — fields the part's category doesn't claim are
   * hidden (a Resistor's diff list won't show `vf` even if the
   * vendor returned one). */
  categories?: readonly PartCategory[],
): Diff[] {
  const partCategorySlug = part.category_id != null && categories
    ? categories.find((c) => c.id === part.category_id)?.slug ?? null
    : null;
  const out: Diff[] = [];
  for (const def of FIELDS) {
    if (isPartFieldHidden(def.partField as string, partCategorySlug)) continue;
    const current = part[def.partField] as unknown;
    const vendor = dto[def.dtoField] as unknown;
    if (valuesEqual(current, vendor)) continue;
    // Skip rows where the vendor has nothing to offer — there's no
    // value in showing "we don't know" as a "change".
    if (isEmpty(vendor)) continue;
    // Pre-check rule: if current is empty, always pre-check ON
    // regardless of severity (filling a blank is unambiguously
    // additive). Otherwise, ON for commercial, OFF for structural
    // and informational.
    const preChecked = isEmpty(current) || def.severity === 'commercial';
    out.push({ def, current, vendor, preChecked });
  }

  // Category is intentionally outside FIELDS — the rubric model is
  // attribute-equality-based and category needs an id↔name dance.
  // The lookup endpoint resolves DTO category_hint + fallbacks
  // server-side and surfaces (category_id, category_name); we render
  // names but apply the id.
  if (categories && dto.category_id != null && part.category_id !== dto.category_id) {
    const currentName = formatCategoryName(part.category_id, categories);
    const vendorName = dto.category_name ?? formatCategoryName(dto.category_id, categories);
    if (vendorName) {
      out.push({
        def: {
          partField: 'category_id' as keyof PartDetail,
          dtoField: 'category_id' as keyof LookupResult,
          label: 'Category',
          severity: 'commercial',
        },
        current: currentName ?? '—',
        vendor: vendorName,
        applyValue: dto.category_id,
        // Pre-check on whether we're filling a blank (always
        // additive) or replacing an existing category (commercial
        // severity default for disagreements).
        preChecked: true,
      });
    }
  }

  return out;
}


/** Render `id` as "Parent › Child" (or just "Child" for top-level)
 * using the categories cache. Returns null when the id doesn't
 * resolve. */
function formatCategoryName(
  id: number | null,
  categories: readonly PartCategory[],
): string | null {
  if (id == null) return null;
  const cat = categories.find((c) => c.id === id);
  if (!cat) return null;
  if (cat.parent_id == null) return cat.name;
  const parent = categories.find((c) => c.id === cat.parent_id);
  return parent ? `${parent.name} › ${cat.name}` : cat.name;
}

export function formatValue(value: unknown, def: FieldDef): string {
  if (def.format) return def.format(value);
  if (isEmpty(value)) return '—';
  if (typeof value === 'number') {
    return def.unit ? `${value}${def.unit}` : String(value);
  }
  return String(value);
}
